import { useEffect } from "react"
import queryString from "query-string"
import { getQ, removeQueryParam } from "../comp/History"

/**
 * Verification emails return the person to the page they were sent from. The API saves
 * that page (path + query) per email when it sends one and hands it back, once, when the
 * email is verified - the link itself carries nothing but the code.
 */

// Reopens a card the URL alone doesn't hold, eg "invite-org:<orgId>"
export const VERIFY_OPEN_PARAM = "verify-open"

// The verification link's own params, never part of the page
const LINK_PARAMS = ["mode", "oobCode", "apiKey", "lang", "continueUrl"]

const CHANNEL = "mx-email-verified"

export const verifyOpenOrgInvite = (orgId) => `invite-org:${orgId}`
export const verifyOpenMissionInvite = (missionId) => `invite-mission:${missionId}`
export const verifyOpenTaskInvite = (taskId) => `invite-task:${taskId}`

/**
 * The page a verification email returns to: this one, plus the card to reopen.
 *
 * @param [open] {string} A verifyOpen* value
 * @param [location] Defaults to window.location
 * @returns {string} path + query + hash
 */
export function getVerifyReturnUrl(open, location = window.location) {
    const q = queryString.parse(location.search)

    LINK_PARAMS.forEach((param) => delete q[param])
    delete q[VERIFY_OPEN_PARAM]

    if (open) q[VERIFY_OPEN_PARAM] = open

    const search = queryString.stringify(q)

    return `${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`
}

/**
 * A returned page as a path inside the app, or null: "//host" or "/\host" would take
 * the browser to another site.
 *
 * @param url {string}
 * @param [origin] Defaults to window.location.origin
 * @returns {string|null}
 */
export function getSafeVerifyReturn(url, origin = window.location.origin) {
    if (typeof url !== "string" || !url.startsWith("/")) return null

    try {
        const parsed = new URL(url, origin)

        return parsed.origin === origin ? `${parsed.pathname}${parsed.search}${parsed.hash}` : null
    } catch (e) {
        return null
    }
}

/**
 * True once, for the card a returned page asked to reopen. The param goes, so a reload
 * or another card on the page doesn't reopen it again.
 *
 * @param value {string} A verifyOpen* value
 * @returns {boolean}
 */
export function claimVerifyOpen(value) {
    if (!value || getQ()[VERIFY_OPEN_PARAM] !== value) return false

    removeQueryParam(VERIFY_OPEN_PARAM)

    return true
}

/**
 * claimVerifyOpen on mount, for function components.
 *
 * @param value {string} A verifyOpen* value
 * @param open {function} Opens the card
 */
export function useVerifyOpen(value, open) {
    useEffect(() => {
        if (claimVerifyOpen(value)) open()
    }, [value])
}

/**
 * Tells the person's other tabs an email was verified, so the one they sent it from
 * unlocks without a reload.
 *
 * @param person {object} The profile the API returned ({ _id, emailVerified, emailList })
 */
export function announceEmailVerified(person) {
    if (typeof BroadcastChannel === "undefined" || !person?._id) return

    try {
        const channel = new BroadcastChannel(CHANNEL)
        channel.postMessage({
            personId: person._id,
            emailVerified: person.emailVerified,
            emailList: person.emailList,
        })
        channel.close()
    } catch (e) {
        // Another tab just keeps its old state until a reload
    }
}

/**
 * @param onVerified {function} Gets { personId, emailVerified, emailList }
 * @returns {function} Stops listening
 */
export function onEmailVerifiedElsewhere(onVerified) {
    if (typeof BroadcastChannel === "undefined") return () => {}

    const channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = (event) => onVerified(event.data || {})

    return () => channel.close()
}
